import React, { useEffect, useState } from "react";
import { getWithTokenAPI } from "../util/Api";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
const PaymentHistory = ({ token }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const data = useSelector((state) => state.user);
  const id = data?.userId?.user?._id;

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        const response = await getWithTokenAPI(
          token,
          `/payment/get_by_id?user_id=${id}`
        );
        setPayments(response?.data?.data?.filter(item => item.status !== "created") || []);

      } catch (err) {
        console.error("Error fetching payment history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentHistory();
  }, [token, id]);

  if (loading) {
    return (
      <div className="w-full p-4 md:p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex space-x-4 mb-4">
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="w-full p-4 md:p-6">
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t("No payments found")}</h3>
          <p className="text-gray-500">{t("You haven't made any payments yet.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h3 className="text-2xl md:text-xl font-bold text-gray-900 mb-2">
         {t("Payment History")}
        </h3>
        <p className="text-gray-600 mt-1"> {t("View all your transaction details and billing information")}</p>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <th className="p-4 text-left text-md font-semibold text-gray-600 uppercase tracking-wider">
                  {t("S.No")}
                </th>
                <th className="p-4 text-left text-md font-semibold text-gray-600 uppercase tracking-wider">
                 {t("Payment ID")}
                </th>
                <th className="p-4 text-left text-md font-semibold text-gray-600 uppercase tracking-wider">
                  {t("Plan")}
                </th>
                <th className="p-4 text-left text-md font-semibold text-gray-600 uppercase tracking-wider">
                   {t("Amount")}
                </th>
                <th className="p-4 text-left text-md font-semibold text-gray-600 uppercase tracking-wider">
                   {t("Status")}
                </th>
                <th className="p-4 text-left text-md font-semibold text-gray-600 uppercase tracking-wider">
                   {t("Start Date")}
                </th>
                <th className="p-4 text-left text-md font-semibold text-gray-600 uppercase tracking-wider">
                   {t("  End Date")}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {payments.map((p, index) => (
                <tr
                  key={p._id}
                  className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                >
                  <td className="p-4 text-sm text-gray-700 font-medium">
                    {index + 1}
                  </td>
                  <td className="p-4">
                    <div className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
                      {p.payment_id || "—"}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm font-semibold text-gray-900">
                      {p.plan_details?.plan_name}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm font-bold text-green-600">
                      ₹{p.plan_details?.price || p.amount / 100}
                    </span>
                  </td>
                  <td className="p-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                        ${p.status === "paid"
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : p.status === "pending"
                            ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                        }
                      `}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-gray-600">
                    {p.subscription?.start_date
                      ? new Date(p.subscription.start_date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })
                      : "—"}
                  </td>
                  <td className="p-4 text-sm text-gray-600">
                    {p.subscription?.end_date
                      ? new Date(p.subscription.end_date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOBILE CARD VIEW - IMPROVED PADDING & ALIGNMENT */}
      <div className="md:hidden space-y-3">
        {payments.map((p, index) => (
          <div
            key={p._id}
            className="bg-white p-4 rounded-xl shadow-sm border border-gray-200"
          >
            {/* Header Row */}
            <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-100">
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-1">
                  {p.plan_details?.plan_name}
                </h3>
                <div className="text-xs font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded inline-block">
                  {p.payment_id || "No ID"}
                </div>
              </div>
              <div className="flex flex-col items-end space-y-1">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold
                    ${p.status === "paid"
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : p.status === "pending"
                        ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }
                  `}
                >
                  {p.status}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  #{index + 1}
                </span>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div>
                  <p className="text-gray-500 text-xs font-medium mb-1">{t("Amount")}</p>
                  <p className="font-bold text-green-600 text-sm">
                    ₹{p.plan_details?.price || p.amount / 100}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 text-xs font-medium mb-1">{t("Start Date")}</p>
                  <p className="text-gray-900 text-sm">
                    {p.subscription?.start_date
                      ? new Date(p.subscription.start_date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })
                      : "—"}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-gray-500 text-xs font-medium mb-1">{t("End Date")}</p>
                  <p className="text-gray-900 text-sm">
                    {p.subscription?.end_date
                      ? new Date(p.subscription.end_date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })
                      : "—"}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 text-xs font-medium mb-1">{t("Duration")}</p>
                  <p className="text-gray-900 text-sm">
                    {p.subscription?.start_date && p.subscription?.end_date
                      ? (() => {
                        const start = new Date(p.subscription.start_date);
                        const end = new Date(p.subscription.end_date);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return `${diffDays} days`;
                      })()
                      : "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentHistory;